import { BookOpen, CalendarDays, Camera, Gamepad2, ListChecks, Settings, Sun } from 'lucide-react';
import type { ComponentType } from 'react';
import { useTheme } from '../../theme/useTheme';
import type { IconSetId } from '../../theme/icons';
import {
  RoundedCalendar,
  RoundedCheckin,
  RoundedDigest,
  RoundedLists,
  RoundedRecipes,
  RoundedSettings,
  type NavIconGlyphProps,
} from './rounded';

type Glyph = ComponentType<NavIconGlyphProps>;

/**
 * Module id → glyph, per set. Keyed by *module id* rather than path or label so a route
 * rename can't silently blank a tab, and deliberately partial: anything not listed falls
 * through to the module's own registered emoji (see `NavIcon` below), so a future module
 * renders something sensible on day one and can add itself here whenever.
 *
 * `'emoji'` has no map at all — it *is* the fallback path.
 */
const GLYPHS: Record<Exclude<IconSetId, 'emoji'>, Record<string, Glyph>> = {
  line: {
    calendar: CalendarDays,
    lists: ListChecks,
    recipes: BookOpen,
    checkin: Camera,
    digest: Sun,
    games: Gamepad2,
    settings: Settings,
  },
  rounded: {
    calendar: RoundedCalendar,
    lists: RoundedLists,
    recipes: RoundedRecipes,
    checkin: RoundedCheckin,
    digest: RoundedDigest,
    games: Gamepad2,
    settings: RoundedSettings,
  },
};

export interface NavIconProps {
  /** Registry module id (`'calendar'`, `'lists'`, …; `'settings'` for the shell's own entry). */
  moduleId: string;
  /** The module's registered `nav.icon` — rendered as-is by the Emoji set and used as the
   * fallback for an unmapped module under the drawn sets. */
  emoji?: string;
  /** Box size in px. Every set renders into the same box, so switching sets never reflows
   * the nav — the tab bar's icon slot is exactly this wide in all three. */
  size?: number;
  /** Overrides the active preference — used by the Settings previews. */
  set?: IconSetId;
}

/**
 * Renders one nav icon in whichever set the device has selected (`awwyeah:icons`).
 *
 * Drawn glyphs are `currentColor` throughout, so the nav's own `text-accent` (active) /
 * `text-fg-3` (inactive) classes tint them, in every theme, with no per-theme code here.
 */
export function NavIcon({ moduleId, emoji, size = 24, set }: NavIconProps): JSX.Element {
  const { iconSet } = useTheme();
  const active = set ?? iconSet;
  const Glyph = active === 'emoji' ? undefined : GLYPHS[active][moduleId];

  return (
    <span
      aria-hidden="true"
      className="inline-flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
    >
      {Glyph ? (
        <Glyph className="h-full w-full" strokeWidth={1.75} />
      ) : (
        // Emoji render optically smaller than a glyph that fills its box, hence the 0.84.
        <span style={{ fontSize: size * 0.84, lineHeight: 1 }}>{emoji ?? '•'}</span>
      )}
    </span>
  );
}
